import {
  CodeBracketIcon,
  DocumentArrowDownIcon,
  DocumentChartBarIcon,
  DocumentIcon,
  DocumentTextIcon,
  FilmIcon,
  MusicalNoteIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { treeStyles, TreeViewSize } from '../constants/treeStyles';

// Map of file categories to their extensions
const fileCategories = {
  images: ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'],
  documents: ['doc', 'docx', 'odt', 'rtf', 'txt', 'md', 'pages', 'tex', 'wpd'],
  spreadsheets: ['xls', 'xlsx', 'csv', 'ods', 'numbers'],
  pdfs: ['pdf'],
  code: [
    'js',
    'jsx',
    'ts',
    'tsx',
    'html',
    'css',
    'scss',
    'json',
    'xml',
    'yaml',
    'yml',
    'py',
    'java',
    'c',
    'cpp',
    'cs',
    'go',
    'php',
    'rb',
    'swift',
    'sh',
    'bash',
  ],
  audio: ['mp3', 'wav', 'ogg', 'flac', 'aac', 'm4a'],
  video: ['mp4', 'webm', 'mov', 'avi', 'mkv', 'wmv', 'flv'],
};

/**
 * Get the appropriate icon for a file based on its extension
 */
export const getFileIcon = (
  fileExtension: string | null | undefined,
  size: TreeViewSize = 'sm',
) => {
  const iconClasses = treeStyles.icons.file;

  if (!fileExtension)
    return (
      <DocumentIcon
        className={`${iconClasses} text-muted-foreground flex-shrink-0`}
      />
    );

  // Remove the dot if present and convert to lowercase
  const ext = fileExtension.replace(/^\./, '').toLowerCase();

  if (fileCategories.images.includes(ext)) {
    return (
      <PhotoIcon className={`${iconClasses} text-blue-400 flex-shrink-0`} />
    );
  }

  if (fileCategories.documents.includes(ext)) {
    return (
      <DocumentTextIcon
        className={`${iconClasses} text-amber-400 flex-shrink-0`}
      />
    );
  }

  if (fileCategories.spreadsheets.includes(ext)) {
    return (
      <DocumentChartBarIcon
        className={`${iconClasses} text-green-400 flex-shrink-0`}
      />
    );
  }

  if (fileCategories.pdfs.includes(ext)) {
    return (
      <DocumentArrowDownIcon
        className={`${iconClasses} text-red-400 flex-shrink-0`}
      />
    );
  }

  if (fileCategories.code.includes(ext)) {
    return (
      <CodeBracketIcon
        className={`${iconClasses} text-purple-400 flex-shrink-0`}
      />
    );
  }

  if (fileCategories.audio.includes(ext)) {
    return (
      <MusicalNoteIcon
        className={`${iconClasses} text-yellow-400 flex-shrink-0`}
      />
    );
  }

  if (fileCategories.video.includes(ext)) {
    return (
      <FilmIcon className={`${iconClasses} text-pink-400 flex-shrink-0`} />
    );
  }

  // Default icon for any other file type
  return (
    <DocumentIcon
      className={`${iconClasses} text-muted-foreground flex-shrink-0`}
    />
  );
};
